// Utility functions to transform Strapi v4 API responses
// to the format expected by the frontend

import {
    CategoryItem,
    SlideshowItem,
    MediaFile,
    CategoryChild,
    HelpSection,
    SocialButton,
    FooterContent
} from '../types';

// Transform Strapi v4 media object
export const transformMediaFile = (strapiMedia: any): MediaFile | undefined => {
    if (!strapiMedia?.data) return undefined;

    const mediaData = strapiMedia.data;
    const attributes = mediaData.attributes || mediaData;

    return {
        id: mediaData.id,
        ...attributes
    };
};

// Transform any nested relation (can be single item or array)
export const transformRelation = (relation: any): any => {
    if (!relation?.data) return undefined;

    if (Array.isArray(relation.data)) {
        return relation.data.map((item: any) => transformStrapiItem(item));
    }

    return transformStrapiItem(relation.data);
};

// Transform single Strapi v4 item to the expected format
export const transformStrapiItem = <T>(strapiItem: any): T => {
    if (!strapiItem) return strapiItem;

    // If it's already in the old format, return as-is
    if (!strapiItem.attributes) {
        return strapiItem;
    }

    const transformed: any = {
        id: strapiItem.id,
        ...strapiItem.attributes
    };

    // Transform all possible relation fields
    const relationFields = [
        'media', 'mobile_media', 'portfolioFile', 'children', 'product',
        'localizations', 'categories', 'products', 'parent', 'icon',
        'image', 'logo', 'avatar', 'thumbnail', 'cover', 'file',
        // Slide-specific media fields
        'background_media', 'content_media', 'left_media', 'right_media', 'slide', 'slide_data',
        // Section relations
        'child_slides'
    ];

    relationFields.forEach(field => {
        if (transformed[field]) {
            const transformedRelation = transformRelation(transformed[field]);
            if (transformedRelation !== undefined) {
                transformed[field] = transformedRelation;
            }
        }
    });

    // Handle component fields (like 'slide' or 'slide_data') - components don't have data wrapper
    // They are directly in attributes
    // For Dynamic Zones (arrays), normalize to single object for backward compatibility
    ['slide', 'slide_data'].forEach(slideField => {
        if (transformed[slideField]) {
            // Check if it's a Dynamic Zone (array of components) - normalize to first item
            if (Array.isArray(transformed[slideField])) {
                const slideArray = transformed[slideField];
                if (slideArray.length > 0) {
                    const firstComponent = slideArray[0];
                    // Transform media fields in the component
                    const transformedComponent: any = { ...firstComponent };
                    
                    // Map __component to layout_type for consistency
                    if (transformedComponent.__component && !transformedComponent.layout_type) {
                        const componentName = transformedComponent.__component.split('.')[1]; // e.g., "slides.full-media" -> "full-media"
                        transformedComponent.layout_type = componentName;
                    }
                    
                    ['media', 'background_media', 'content_media', 'left_media', 'right_media'].forEach(mediaField => {
                        if (transformedComponent[mediaField]?.data) {
                            transformedComponent[mediaField] = transformMediaFile(transformedComponent[mediaField]);
                        }
                    });
                    
                    // Replace array with single normalized object
                    transformed[slideField] = transformedComponent;
                }
            }
            // Single component (old format)
            else if (typeof transformed[slideField] === 'object' && !transformed[slideField].data) {
                const slideComponent = transformed[slideField];
                ['media', 'background_media', 'content_media', 'left_media', 'right_media'].forEach(mediaField => {
                    if (slideComponent[mediaField]?.data) {
                        slideComponent[mediaField] = transformMediaFile(slideComponent[mediaField]);
                    }
                });
            }
        }
    });

    return transformed as T;
};

// Transform Strapi v4 API response
export const transformStrapiResponse = <T>(strapiResponse: any): { data: T; meta?: any } => {
    if (!strapiResponse) {
        return { data: null as T };
    }

    // If it's already in the old format, return as-is
    if (!strapiResponse.data || Array.isArray(strapiResponse.data) && strapiResponse.data.length > 0 && !strapiResponse.data[0]?.attributes) {
        return strapiResponse;
    }

    let transformedData: any;

    if (Array.isArray(strapiResponse.data)) {
        // Handle array responses (collections)
        transformedData = strapiResponse.data.map((item: any) => transformStrapiItem(item));
    } else if (strapiResponse.data) {
        // Handle single item responses
        transformedData = transformStrapiItem(strapiResponse.data);
    } else {
        transformedData = null;
    }

    return {
        data: transformedData as T,
        meta: strapiResponse.meta
    };
};

// Specific transformers for different content types
export const transformCategoryItem = (strapiItem: any): CategoryItem => {
    return transformStrapiItem<CategoryItem>(strapiItem);
};

export const transformSlideshowItem = (strapiItem: any): SlideshowItem => {
    return transformStrapiItem<SlideshowItem>(strapiItem);
};

export const transformCategoryChild = (strapiItem: any): CategoryChild => {
    return transformStrapiItem<CategoryChild>(strapiItem);
};

export const transformHelpSection = (strapiItem: any): HelpSection => {
    return transformStrapiItem<HelpSection>(strapiItem);
};

export const transformSocialButton = (strapiItem: any): SocialButton => {
    return transformStrapiItem<SocialButton>(strapiItem);
};

export const transformFooterContent = (strapiItem: any): FooterContent => {
    return transformStrapiItem<FooterContent>(strapiItem);
};